import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import EasyCropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ItemCardUserImage } from '../../types/pointItemNote';
import {
  deleteItemCardImage,
  newItemCardImageId,
  uploadItemCardImage,
} from '../../services/itemCardImageStorageService';
import {
  ITEM_CARD_BASIC_OPTIONS,
  ITEM_CARD_USER_IMAGE_MAX,
  buildItemCardImageSlots,
  flattenItemCardImageSlots,
  getIconBasicImagePath,
  isNumericCardImage,
  itemCardSlotsSignature,
  type ItemCardImageLocalSlot,
} from '../../utils/itemCardImage';
import {
  cropRectToWebp,
  readFileAsDataURL,
  type CropAreaPixels,
} from '../../utils/imageCompress';
import { readClipboardImageBlob } from '../../utils/slideImageCompress';
import ItemCardStackIcon from './ItemCardStackIcon';
import '../../pages/AdminProfileCardSetsPage.css';

const Cropper = EasyCropper as unknown as React.ComponentType<{
  image?: string;
  crop: { x: number; y: number };
  zoom: number;
  aspect: number;
  cropShape?: 'rect' | 'round';
  showGrid?: boolean;
  onCropChange: (location: { x: number; y: number }) => void;
  onZoomChange?: (zoom: number) => void;
  onCropComplete?: (croppedArea: CropAreaPixels, croppedAreaPixels: CropAreaPixels) => void;
}>;

type CardImageTab = 'none' | 'default' | 'user';

export type ItemCardImageSelectModalProps = {
  open: boolean;
  currentCardImage?: string;
  userImages: ItemCardUserImage[];
  onClose: () => void;
  /** 선택 적용: ''/'default' | '1'..'n' | url */
  onApply: (cardImage: string) => void;
  onUserImagesChange: (images: ItemCardUserImage[]) => void;
};

const OUT_SIZE = 144;

type SlotUploadTarget = {
  /** 클릭한 슬롯 인덱스(0..39) */
  slotIndex: number;
  /** 있으면 해당 슬롯 이미지 교체 */
  replaceId: string | null;
  title: string;
};

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SlotUploadModal: React.FC<{
  target: SlotUploadTarget | null;
  onClose: () => void;
  onSave: (blob: Blob) => Promise<void>;
}> = ({ target, onClose, onSave }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cropPixelsRef = useRef<CropAreaPixels | null>(null);
  const [rawImageSrc, setRawImageSrc] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setRawImageSrc('');
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setIsSaving(false);
    setIsDragging(false);
    setError('');
    cropPixelsRef.current = null;
  }, [target]);

  const handleFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('이미지 파일만 업로드할 수 있습니다.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('이미지 파일은 10MB 이하만 업로드할 수 있습니다.');
      return;
    }
    setError('');
    setRawImageSrc(await readFileAsDataURL(file));
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    cropPixelsRef.current = null;
  }, []);

  useEffect(() => {
    if (!target || rawImageSrc) return undefined;
    const onPaste = (e: ClipboardEvent) => {
      const blob = readClipboardImageBlob(e.clipboardData);
      if (!blob) return;
      e.preventDefault();
      e.stopPropagation();
      const file =
        blob instanceof File
          ? blob
          : new File([blob], 'clipboard-image.png', { type: blob.type || 'image/png' });
      void handleFile(file);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [target, rawImageSrc, handleFile]);

  if (!target) return null;

  const handleSave = async () => {
    if (!rawImageSrc || !cropPixelsRef.current || isSaving) return;
    setIsSaving(true);
    setError('');
    try {
      const blob = await cropRectToWebp(
        rawImageSrc,
        cropPixelsRef.current,
        OUT_SIZE,
        OUT_SIZE,
        0.8
      );
      await onSave(blob);
      // 등록 버튼 클릭이 뒤 오버레이로 전달되어 선택 모달이 닫히지 않도록 다음 틱에 닫기
      window.setTimeout(() => onClose(), 0);
    } catch (e) {
      const msg = e instanceof Error ? e.message : '';
      setError(
        msg === 'QUOTA_EXCEEDED'
          ? '이미지 저장 용량이 부족합니다. 사용량을 확인한 뒤 다시 시도해 주세요.'
          : '이미지 저장에 실패했습니다. 다시 시도해 주세요.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return createPortal(
    <div className="cp-pc-upload-overlay" role="presentation" onClick={onClose}>
      <div
        className="cp-pc-upload-modal"
        role="dialog"
        aria-modal="true"
        aria-label={target.title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cp-pc-upload-header">
          <h3>{target.title}</h3>
          <button type="button" className="cp-pc-upload-close" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </div>

        {!rawImageSrc ? (
          <label
            className={`cp-pc-upload-dropzone${isDragging ? ' is-dragging' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              const file = e.dataTransfer.files?.[0];
              if (file) void handleFile(file);
            }}
          >
            <span>이미지를 드래그·클릭하거나 Ctrl+V로 붙여넣기</span>
            <span className="cp-pc-upload-dropzone-hint">
              정사각형으로 자를 수 있습니다 · JPG/PNG/WebP · 10MB 이하
            </span>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void handleFile(file);
              }}
            />
          </label>
        ) : (
          <div className="cp-pc-upload-crop">
            <Cropper
              image={rawImageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="rect"
              showGrid
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_area, pixels) => {
                cropPixelsRef.current = pixels;
              }}
            />
          </div>
        )}

        {error ? <p className="cp-pc-upload-error">{error}</p> : null}

        {rawImageSrc ? (
          <label className="cp-pc-upload-zoom">
            <span>확대</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
            />
          </label>
        ) : null}

        <div className="cp-pc-upload-actions">
          <button
            type="button"
            className="cp-pc-upload-btn is-cancel"
            onClick={() => {
              if (rawImageSrc) {
                setRawImageSrc('');
                cropPixelsRef.current = null;
                return;
              }
              onClose();
            }}
            disabled={isSaving}
          >
            {rawImageSrc ? '다시 선택' : '취소'}
          </button>
          <button
            type="button"
            className="cp-pc-upload-btn is-save"
            onClick={() => void handleSave()}
            disabled={!rawImageSrc || isSaving}
          >
            {isSaving ? '등록 중…' : '등록'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SortableSlot: React.FC<{
  slot: ItemCardImageLocalSlot;
  index: number;
  selected: boolean;
  deleting: boolean;
  reorderMode: boolean;
  onSelect: () => void;
  onEmptyClick: () => void;
  onDelete: (e: React.MouseEvent) => void;
  onReplace: () => void;
}> = ({
  slot,
  index,
  selected,
  deleting,
  reorderMode,
  onSelect,
  onEmptyClick,
  onDelete,
  onReplace,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: slot.key,
    disabled: !reorderMode,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 3 : undefined,
  };
  const filled = slot.image != null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`cp-pin-card-img-slot${filled ? ' has-image' : ' is-empty'}${
        selected ? ' is-selected' : ''
      }${isDragging ? ' is-dragging' : ''}${reorderMode ? ' is-reorder' : ''}`}
      {...attributes}
      {...(reorderMode ? listeners : {})}
      onClick={() => {
        if (reorderMode) return;
        if (filled) onSelect();
        else onEmptyClick();
      }}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      aria-label={
        reorderMode
          ? `${index + 1}번 슬롯 드래그하여 순서 변경`
          : filled
            ? `${index + 1}번 이미지 선택`
            : `${index + 1}번 슬롯에 이미지 등록`
      }
      onKeyDown={(e) => {
        if (reorderMode) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (filled) onSelect();
          else onEmptyClick();
        }
      }}
    >
      <div className={`cp-pin-card-img-slot-media${filled ? '' : ' is-empty'}`}>
        {filled ? (
          <img src={slot.image!.url} alt="" draggable={false} />
        ) : (
          <span className="cp-pin-card-img-slot-plus" aria-hidden>
            +
          </span>
        )}
        {filled && !reorderMode ? (
          <>
            <button
              type="button"
              className="cp-pin-card-img-slot-fab is-edit"
              title="이미지 수정"
              aria-label="이미지 수정"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onReplace();
              }}
            >
              <EditIcon />
            </button>
            <button
              type="button"
              className="cp-pin-card-img-slot-fab is-delete"
              title="이미지 삭제"
              aria-label="이미지 삭제"
              disabled={deleting}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(e);
              }}
            >
              {deleting ? '…' : <TrashIcon />}
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
};

const ItemCardImageSelectModal: React.FC<ItemCardImageSelectModalProps> = ({
  open,
  currentCardImage,
  userImages,
  onClose,
  onApply,
  onUserImagesChange,
}) => {
  const lastClickTimeRef = useRef(0);
  const lastClickValueRef = useRef<string | null>(null);

  const [tab, setTab] = useState<CardImageTab>('default');
  const [selected, setSelected] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [uploadTarget, setUploadTarget] = useState<SlotUploadTarget | null>(null);
  const [localSlots, setLocalSlots] = useState<ItemCardImageLocalSlot[]>(() =>
    buildItemCardImageSlots(userImages)
  );
  const [reorderMode, setReorderMode] = useState(false);
  const [orderDirty, setOrderDirty] = useState(false);
  const backdropPointerRef = useRef(false);
  const orderBaselineRef = useRef('');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );

  const filledCount = useMemo(
    () => localSlots.filter((s) => s.image != null).length,
    [localSlots]
  );

  useEffect(() => {
    if (!open) return;
    const cur = (currentCardImage ?? '').trim();
    if (!cur || cur === 'default') {
      setTab('none');
      setSelected('');
    } else if (isNumericCardImage(cur)) {
      setTab('default');
      setSelected(cur);
    } else {
      setTab('user');
      setSelected(cur);
    }
    const slots = buildItemCardImageSlots(userImages);
    setLocalSlots(slots);
    setReorderMode(false);
    setOrderDirty(false);
    orderBaselineRef.current = itemCardSlotsSignature(slots);
    setDeletingId(null);
    setError('');
    setUploadTarget(null);
    lastClickTimeRef.current = 0;
    lastClickValueRef.current = null;
  }, [open, currentCardImage]);

  useEffect(() => {
    if (!open || reorderMode || orderDirty) return;
    const slots = buildItemCardImageSlots(userImages);
    setLocalSlots(slots);
    orderBaselineRef.current = itemCardSlotsSignature(slots);
  }, [userImages, open, reorderMode, orderDirty]);

  const commitSlots = (next: ItemCardImageLocalSlot[]) => {
    setLocalSlots(next);
    orderBaselineRef.current = itemCardSlotsSignature(next);
    setOrderDirty(false);
    onUserImagesChange(flattenItemCardImageSlots(next));
  };

  const pickOption = (value: string, applyNow?: boolean) => {
    if (reorderMode) return;
    const now = Date.now();
    const isDouble =
      lastClickValueRef.current === value && now - lastClickTimeRef.current < 350;
    setSelected(value);
    if (applyNow || isDouble) {
      onApply(value);
      onClose();
      lastClickValueRef.current = null;
      lastClickTimeRef.current = 0;
      return;
    }
    lastClickValueRef.current = value;
    lastClickTimeRef.current = now;
  };

  const openUploadAtSlot = (slotIndex: number) => {
    if (reorderMode) return;
    const slot = localSlots[slotIndex];
    if (!slot) return;
    if (!slot.image && filledCount >= ITEM_CARD_USER_IMAGE_MAX) {
      setError(
        `사용자 이미지는 최대 ${ITEM_CARD_USER_IMAGE_MAX}개까지 등록할 수 있습니다. 기존 이미지를 삭제한 뒤 올려 주세요.`
      );
      return;
    }
    setError('');
    setUploadTarget({
      slotIndex,
      replaceId: slot.image?.id ?? null,
      title: slot.image ? '카드 이미지 다시 올리기' : `${slotIndex + 1}번 칸 이미지 등록`,
    });
  };

  const handleUploadSave = async (blob: Blob) => {
    if (!uploadTarget) return;
    const { slotIndex, replaceId } = uploadTarget;
    const uploaded = await uploadItemCardImage(blob);
    const nextImage: ItemCardUserImage = {
      id: replaceId ?? newItemCardImageId(),
      path: uploaded.path,
      url: uploaded.url,
      createdAt: new Date().toISOString(),
      slotIndex,
    };

    const next = localSlots.map((slot, i) => {
      if (i !== slotIndex) return slot;
      if (replaceId && slot.image && slot.image.path !== uploaded.path) {
        void deleteItemCardImage(slot.image.path).catch(() => undefined);
      }
      return { key: nextImage.id, image: nextImage };
    });
    commitSlots(next);
    setSelected(nextImage.url);
  };

  const handleDeleteLibrary = async (slotIndex: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const slot = localSlots[slotIndex];
    if (!slot?.image || deletingId || reorderMode) return;
    if (!window.confirm('이 이미지를 라이브러리에서 삭제할까요?')) return;
    setDeletingId(slot.image.id);
    setError('');
    try {
      await deleteItemCardImage(slot.image.path);
      const clearedUrl = slot.image.url;
      const next = localSlots.map((s, i) =>
        i === slotIndex
          ? { key: `empty-${i}-${Math.random().toString(36).slice(2, 8)}`, image: null }
          : s
      );
      commitSlots(next);
      if (selected === clearedUrl) setSelected('');
    } catch (err) {
      console.error(err);
      setError('이미지 삭제에 실패했습니다.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (!reorderMode) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = localSlots.findIndex((x) => x.key === active.id);
    const newIndex = localSlots.findIndex((x) => x.key === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(localSlots, oldIndex, newIndex);
    setLocalSlots(next);
    setOrderDirty(itemCardSlotsSignature(next) !== orderBaselineRef.current);
  };

  const handleReorderButton = () => {
    if (!reorderMode) {
      setReorderMode(true);
      setOrderDirty(false);
      orderBaselineRef.current = itemCardSlotsSignature(localSlots);
      return;
    }
    if (orderDirty) {
      commitSlots(localSlots);
      setReorderMode(false);
      return;
    }
    setLocalSlots(buildItemCardImageSlots(userImages));
    setReorderMode(false);
    setOrderDirty(false);
  };

  const handleApply = () => {
    if (reorderMode && orderDirty) {
      commitSlots(localSlots);
      setReorderMode(false);
    }
    if (tab === 'none') {
      onApply('default');
    } else {
      onApply(selected || 'default');
    }
    onClose();
  };

  if (!open) return null;

  return createPortal(
    <div
      className="cp-pin-card-img-overlay"
      onMouseDown={(e) => {
        backdropPointerRef.current =
          !uploadTarget && e.target === e.currentTarget;
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (!backdropPointerRef.current) return;
        if (e.target !== e.currentTarget) return;
        backdropPointerRef.current = false;
        onClose();
      }}
      role="presentation"
    >
      <div
        className="cp-pin-card-img-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-pin-card-img-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cp-pin-card-img-header">
          <h3 id="cp-pin-card-img-title">카드 이미지 선택</h3>
          <button type="button" className="cp-pin-card-img-close" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </div>

        <div className="cp-pin-card-img-tabs" role="tablist" aria-label="이미지 종류">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'none'}
            className={`cp-pin-card-img-tab${tab === 'none' ? ' is-active' : ''}`}
            onClick={() => {
              setTab('none');
              setSelected('');
              setReorderMode(false);
              setOrderDirty(false);
            }}
          >
            이미지 없음
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'default'}
            className={`cp-pin-card-img-tab${tab === 'default' ? ' is-active' : ''}`}
            onClick={() => {
              setTab('default');
              setReorderMode(false);
              setOrderDirty(false);
            }}
          >
            기본 이미지
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'user'}
            className={`cp-pin-card-img-tab${tab === 'user' ? ' is-active' : ''}`}
            onClick={() => setTab('user')}
          >
            사용자 이미지
          </button>
        </div>

        <button type="button" className="cp-pin-card-img-apply" onClick={handleApply}>
          선택 적용하기
        </button>

        {tab === 'user' ? (
          <button
            type="button"
            className={`cp-pin-card-img-reorder-btn${reorderMode ? ' is-active' : ''}${
              orderDirty ? ' is-dirty' : ''
            }`}
            onClick={handleReorderButton}
          >
            {reorderMode && orderDirty
              ? '순서 변경 저장'
              : reorderMode
                ? '순서 변경 취소'
                : '순서 변경'}
          </button>
        ) : null}

        {error ? (
          <p className="cp-pin-card-img-error" role="alert">
            {error}
          </p>
        ) : null}

        {tab === 'none' ? (
          <div className="cp-pin-card-img-grid">
            <button
              type="button"
              className="cp-pin-card-img-option is-selected is-none"
              aria-pressed
              aria-label="이미지 없음 미리보기"
              onClick={() => {
                onApply('default');
                onClose();
              }}
            >
              <span className="cp-pin-card-img-none-preview" aria-hidden>
                <ItemCardStackIcon size={56} />
              </span>
            </button>
          </div>
        ) : tab === 'default' ? (
          <div className="cp-pin-card-img-grid">
            {ITEM_CARD_BASIC_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                className={`cp-pin-card-img-option${selected === option ? ' is-selected' : ''}`}
                onClick={() => pickOption(option)}
                aria-pressed={selected === option}
                aria-label={`기본 이미지 ${option}`}
              >
                <img src={getIconBasicImagePath(option)} alt="" />
              </button>
            ))}
          </div>
        ) : (
          <div className="cp-pin-card-img-user">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext
                items={localSlots.map((x) => x.key)}
                strategy={rectSortingStrategy}
              >
                <div className="cp-pin-card-img-slot-grid">
                  {localSlots.map((slot, index) => (
                    <SortableSlot
                      key={slot.key}
                      slot={slot}
                      index={index}
                      selected={Boolean(slot.image && selected === slot.image.url)}
                      deleting={deletingId != null && deletingId === slot.image?.id}
                      reorderMode={reorderMode}
                      onSelect={() => {
                        if (slot.image) pickOption(slot.image.url);
                      }}
                      onEmptyClick={() => openUploadAtSlot(index)}
                      onDelete={(e) => void handleDeleteLibrary(index, e)}
                      onReplace={() => openUploadAtSlot(index)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}
      </div>

      <SlotUploadModal
        target={uploadTarget}
        onClose={() => setUploadTarget(null)}
        onSave={handleUploadSave}
      />
    </div>,
    document.body
  );
};

export default ItemCardImageSelectModal;
